import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { Settings, Save, Power, DollarSign } from 'lucide-react';

const AdminSystemSettings = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/admin/config');
      setConfig(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch config:', err);
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    setSaving(true);
    try {
      await api.post('/admin/config', null, { params: { key, value } });
      setConfig({ ...config, [key]: value });
    } catch (err) {
      alert('Failed to update setting');
    }
    setSaving(false);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600 mt-1">Configure platform behavior and features</p>
        </div>

        {/* Feature Flags */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Feature Flags</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Commissions Enabled</p>
                <p className="text-sm text-gray-600">Enable/disable commission calculations globally</p>
              </div>
              <button
                onClick={() => handleToggle('commissions_enabled', !config.commissions_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.commissions_enabled ? 'bg-success' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.commissions_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Withdrawals Enabled</p>
                <p className="text-sm text-gray-600">Enable/disable withdrawal requests</p>
              </div>
              <button
                onClick={() => handleToggle('withdrawals_enabled', !config.withdrawals_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.withdrawals_enabled ? 'bg-success' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.withdrawals_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Maintenance Mode</p>
                <p className="text-sm text-gray-600">Put platform in maintenance mode</p>
              </div>
              <button
                onClick={() => handleToggle('maintenance_mode', !config.maintenance_mode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.maintenance_mode ? 'bg-error' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.maintenance_mode ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Bank Account Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Account Name</p>
              <p className="font-semibold text-gray-900">{config.bank_account_name}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Account Number</p>
              <p className="font-mono text-gray-900">{config.bank_account_number}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">IFSC Code</p>
              <p className="font-mono text-gray-900">{config.bank_ifsc}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">UPI ID</p>
              <p className="font-mono text-gray-900">{config.upi_id}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminSystemSettings;